import { useMemo } from 'react';
import { CalendarEvent } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import dayjs from 'dayjs';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { InsuranceTabHooks, InsuranceTabStore } from '@frontend/insurance-tab';
import { getFullName } from '@frontend/name';
import { FilterableCheckboxListItem } from '../../components';
import { MAX_CALENDAR_LOCATION_COUNT } from '../../constants';
import { useCalendarLocationIds, useCalendarStore } from '../../stores';
import { getTimezoneDetails } from '../../utils/date-helpers';
import { getInsuranceStatusesFilterList } from '../../views/Calendar/helpers';
import { APPT_STATUS_ALL_OPTION, useGetAppointmentStatusList } from '../use-get-appointment-status-list';
import { useSchedulingLocationInfo } from '../use-scheduling-location-info';
import { useGetOperatoryV3Data } from './use-get-operatory-v3-data';

type InsuranceStatusFilterType = {
  hasInsuranceStatusFilter: boolean;
  filteredPersonIdsByInsuranceStatus: string[];
};

export const getFilteredCalendarEvents = (
  events: CalendarEvent[],
  providerIds: string[],
  apptTypeIds: string[],
  apptStatuses: string[],
  operatoryIds: string[],
  insuranceStatusFilter: InsuranceStatusFilterType
): CalendarEvent[] => {
  const hasProviderFilter = providerIds.length > 0;
  const hasApptTypeFilter = apptTypeIds.length > 0;
  const hasApptStatusFilter = apptStatuses.length > 0;
  const hasOperatoryFilter = operatoryIds.length > 0;
  const hasInsuranceStatusFilter = insuranceStatusFilter.hasInsuranceStatusFilter;

  if (
    !hasProviderFilter &&
    !hasApptTypeFilter &&
    !hasApptStatusFilter &&
    !hasOperatoryFilter &&
    !hasInsuranceStatusFilter
  ) {
    return events;
  }

  return events.filter((event) => {
    // Normalize calendar event data
    const attendeeStatus = event?.attendeeStatus?.toLowerCase().replaceAll('_', ' ') || '';
    const providerId = event?.organizerId || '';
    const appointmentTypeId = event?.referenceTypeId || '';
    const operatoryId = event?.operatoryId || '';
    const attendeeId = event?.attendeeId || '';

    const matchesOperatory = hasOperatoryFilter ? operatoryIds.includes(operatoryId) : true;
    const matchesProvider = hasProviderFilter ? providerIds.includes(providerId) : true;
    const matchesApptType = hasApptTypeFilter ? apptTypeIds.includes(appointmentTypeId) : true;
    const matchesApptStatus = hasApptStatusFilter
      ? apptStatuses.some((status) => status.toLowerCase().replaceAll('_', ' ') === attendeeStatus)
      : true;

    const matchesAttendeeId = hasInsuranceStatusFilter
      ? insuranceStatusFilter?.filteredPersonIdsByInsuranceStatus?.includes(attendeeId)
      : true;

    return matchesProvider && matchesApptType && matchesApptStatus && matchesOperatory && matchesAttendeeId;
  });
};

export const useGetCalendarV3QueriesData = () => {
  const { selectedLocationIds } = useSchedulingLocationInfo();
  const { locationTimezoneMap } = SchedulerV3.Helpers.useScheduleLocationTimezoneShallowStore('locationTimezoneMap');
  const { calendarLocationIds: _calendarLocationIds } = useCalendarLocationIds();

  const { isForSchedulePulse, calendarDate } = useCalendarStore(['isForSchedulePulse', 'calendarDate']);
  const locationIds = isForSchedulePulse ? selectedLocationIds : _calendarLocationIds;

  const calendarTodayDate = useMemo(
    () => getTimezoneDetails(locationTimezoneMap[locationIds[0]]?.timezone).todayDate,
    [locationIds, locationTimezoneMap]
  );

  //#region Queries
  const appointmentStatusListData = useGetAppointmentStatusList({
    locationIds: locationIds,
  });
  const { data: appointmentTypesData } = SchedulerV3.Helpers.useGetAppointmentTypesV3Data({
    selectedLocationId: locationIds[0],
    selectedLocationIds: locationIds,
    pageConfig: {
      page: 1,
      limit: SchedulerV3.Constants.CALENDAR_VIEW_V3_API_QUERY_LIMIT,
    },
  });
  const { data: providersData } = SchedulerV3.Helpers.useGetProvidersV3Data({
    selectedLocationId: locationIds[0],
    selectedLocationIds: locationIds,
    pageConfig: {
      page: 1,
      limit: SchedulerV3.Constants.CALENDAR_VIEW_V3_API_QUERY_LIMIT,
    },
  });
  const { data: operatoryData } = useGetOperatoryV3Data({
    selectedLocationId: locationIds[0],
    selectedLocationIds: locationIds,
    pageConfig: { page: 1, limit: SchedulerV3.Constants.CALENDAR_VIEW_V3_API_QUERY_LIMIT },
  });

  const { calendarEventsData, filteredCalendarEventsData, isLoadingCalendarEvents } =
    useGetFilteredCalendarEventsV3(locationIds);
  //#endregion Queries

  //#region Calendar Events Data Processing
  const calendarEventsCount = filteredCalendarEventsData.length || 0;
  const unconfirmedCalendarEventsCount = useMemo(
    () =>
      filteredCalendarEventsData.filter((appointment) => appointment.attendeeStatus?.toLowerCase() === 'unconfirmed')
        .length || 0,
    [filteredCalendarEventsData]
  );
  //#endregion Calendar Events Data Processing

  //#region Filter Lists
  const apptStatusList = useMemo<FilterableCheckboxListItem[]>(() => {
    return appointmentStatusListData
      .filter((item) => item !== APPT_STATUS_ALL_OPTION)
      .map((item) => ({
        label: item,
        value: item,
      }));
  }, [appointmentStatusListData]);
  const apptTypeList = useMemo<FilterableCheckboxListItem[]>(() => {
    const allApptTypes = (appointmentTypesData?.appointmentTypes ?? []).map((apptType) => ({
      label: apptType.externalName || apptType.displayName || '',
      value: apptType.id,
      isArchived: !apptType.details?.act,
    }));

    // Remove duplicates, then sort by isArchived (false first), then by label
    return Array.from(new Map(allApptTypes.map((at) => [at.value, at])).values()).sort((a, b) => {
      const aArchived = !!a.isArchived;
      const bArchived = !!b.isArchived;

      if (aArchived !== bArchived) {
        return Number(aArchived) - Number(bArchived); // false (active) before true (archived)
      }
      return a.label.localeCompare(b.label);
    });
  }, [appointmentTypesData?.appointmentTypes]);
  const providerList = useMemo<FilterableCheckboxListItem[]>(() => {
    const allProviders = (providersData?.providers ?? []).map((practitioner) => ({
      label: getFullName(practitioner),
      value: practitioner.id,
      isArchived: practitioner.isArchived,
    }));

    // Remove duplicates, then sort by isArchived (false first), then by label
    return Array.from(new Map(allProviders.map((p) => [p.value, p])).values()).sort((a, b) => {
      // Normalize undefined/null to false for consistent sorting
      const aArchived = !!a.isArchived;
      const bArchived = !!b.isArchived;

      if (aArchived !== bArchived) {
        return Number(aArchived) - Number(bArchived); // false (active) before true (archived)
      }
      return a.label.localeCompare(b.label);
    });
  }, [locationIds, providersData?.providers]);

  const operatoryList = useMemo<FilterableCheckboxListItem[]>(() => {
    const allOperatories = (operatoryData?.operatories ?? []).map((operatory) => ({
      label: operatory.displayName || operatory.externalName || '',
      value: operatory.id,
    }));

    // Ignore duplicate operatories across locations and sort by name
    return Array.from(new Map(allOperatories.map((operatory) => [operatory.value, operatory])).values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [locationIds, operatoryData?.operatories]);
  //#endregion Filter Lists

  // Insurance Count
  const { calendarInsuranceCount } = InsuranceTabHooks.useGetInsuranceStatuses({
    appointments: calendarEventsData?.events || [],
    scheduleSelectedLocationIds: locationIds,
    isCalendarView: true,
    calendarDate: calendarDate,
  });

  const insuranceStatuses = useMemo(() => Array.from(getInsuranceStatusesFilterList().values()), []);

  return {
    calendarLocationIds: locationIds,
    calendarTodayDate,

    appointmentStatusListData,
    appointmentTypesData,
    providersData,
    operatoryData,

    calendarEventsData,
    filteredCalendarEventsData,
    isLoadingCalendarEvents,

    calendarCounts: {
      unconfirmed: unconfirmedCalendarEventsCount,
      total: calendarEventsCount,
      insurance: calendarInsuranceCount,
    },
    filterLists: {
      appointmentStatuses: apptStatusList,
      appointmentTypes: apptTypeList,
      providers: providerList,
      operatories: operatoryList,
      insuranceStatuses,
    },
  };
};

export const useGetFilteredCalendarEventsV3 = (locationIds: string[]) => {
  const {
    calendarDate,
    calendarProviderIds,
    calendarApptTypeIds,
    calendarApptStatuses,
    calendarOperatories,
    calendarInsuranceStatuses,
  } = useCalendarStore([
    'calendarDate',
    'calendarProviderIds',
    'calendarApptTypeIds',
    'calendarApptStatuses',
    'calendarOperatories',
    'calendarInsuranceStatuses',
  ]);

  const { insuranceStatuses } = InsuranceTabStore.useGetInsuranceStatusesStoreShallow('insuranceStatuses');

  const formattedCalendarDate = dayjs(calendarDate).format('MM/DD/YYYY');

  const selectedCalendarInsuranceStatuses = useMemo(() => {
    return new Set(calendarInsuranceStatuses.map((status) => status?.toLowerCase()));
  }, [calendarInsuranceStatuses]);

  const filteredPersonIdBySelectedCalendarInsuranceStatuses = useMemo(() => {
    const personIds: string[] = [];

    locationIds.forEach((locationId) => {
      const insuranceStatusesObjectByPersonId =
        insuranceStatuses?.[formattedCalendarDate]?.locationVerificationCounts?.[locationId]?.coverageStatusByPersonId;

      // If the insuranceStatuses Object is found then filter data by insurance status and append values into personId
      if (insuranceStatusesObjectByPersonId) {
        const matchingKeys = Object.keys(insuranceStatusesObjectByPersonId).filter((key) => {
          const insuranceStatus = insuranceStatusesObjectByPersonId[key];
          return (
            typeof insuranceStatus === 'string' && selectedCalendarInsuranceStatuses.has(insuranceStatus?.toLowerCase())
          );
        });
        personIds.push(...matchingKeys);
      }
    });

    if (personIds.length) {
      return Array.from(new Set(personIds));
    }

    return [];
  }, [formattedCalendarDate, insuranceStatuses, locationIds, selectedCalendarInsuranceStatuses]);

  const { data: calendarEventsData, isLoading: isLoadingCalendarEvents } =
    SchedulerV3.Helpers.useGetCalendarEventsV3Data({
      selectedDate: calendarDate,
      selectedLocationIds: locationIds,
      page: 1,
      limit: SchedulerV3.Constants.CALENDAR_VIEW_V3_API_QUERY_LIMIT,
      enabled: locationIds.length > 0 && locationIds.length <= MAX_CALENDAR_LOCATION_COUNT,
    });

  const filteredCalendarEventsData = useMemo(() => {
    if (
      !!calendarProviderIds.length ||
      !!calendarApptTypeIds.length ||
      !!calendarApptStatuses.length ||
      !!calendarOperatories.length ||
      !!calendarInsuranceStatuses.length
    ) {
      const insuranceStatusFilter = {
        hasInsuranceStatusFilter: !!calendarInsuranceStatuses.length,
        filteredPersonIdsByInsuranceStatus: filteredPersonIdBySelectedCalendarInsuranceStatuses,
      };
      return getFilteredCalendarEvents(
        calendarEventsData?.events ?? [],
        calendarProviderIds,
        calendarApptTypeIds,
        calendarApptStatuses,
        calendarOperatories,
        insuranceStatusFilter
      );
    }
    return calendarEventsData?.events ?? [];
  }, [
    calendarProviderIds,
    calendarApptTypeIds,
    calendarApptStatuses,
    calendarOperatories,
    filteredPersonIdBySelectedCalendarInsuranceStatuses,
    calendarEventsData?.events,
    calendarInsuranceStatuses.length,
  ]);

  return {
    calendarEventsData,
    filteredCalendarEventsData,
    isLoadingCalendarEvents,
  };
};
